import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, MapPin, Building2, Award, ChevronRight, ArrowRight, ShieldCheck, Activity, Stethoscope } from 'lucide-react';
import MetaTags from '@/components/SEO/MetaTags';
import { US_STATES, US_REGIONS, TOTAL_US_NCI_CENTERS } from '@/data/usStatesData';
import { SITE_ORIGIN } from '@/config/site';

export default function USStateHub() {
  const [search, setSearch] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('All');

  const filteredStates = useMemo(() => {
    return US_STATES.filter((state) => {
      const matchesSearch =
        state.name.toLowerCase().includes(search.toLowerCase()) ||
        state.abbr.toLowerCase().includes(search.toLowerCase()) ||
        state.majorCities.some((c) => c.toLowerCase().includes(search.toLowerCase())) ||
        state.nciCenters.some((c) => c.name.toLowerCase().includes(search.toLowerCase()));

      const matchesRegion = selectedRegion === 'All' || state.region === selectedRegion;
      return matchesSearch && matchesRegion;
    });
  }, [search, selectedRegion]);

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'MedicalWebPage',
      name: 'Best Cancer Centers in the United States by State | ByOnco Care Navigation',
      description: 'Comprehensive directory of top NCI-Designated Comprehensive Cancer Centers and oncology hospitals across all 50 US states. Find expert cancer treatment, second opinions, and clinical trials.',
      url: `${SITE_ORIGIN}/cancer-centers-by-state`,
      about: {
        '@type': 'MedicalCondition',
        name: 'Cancer',
      },
      provider: {
        '@type': 'Organization',
        name: 'ByOnco Care Navigation',
        url: SITE_ORIGIN,
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_ORIGIN },
        { '@type': 'ListItem', position: 2, name: 'US Cancer Centers Directory', item: `${SITE_ORIGIN}/cancer-centers-by-state` },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-cream-paper text-forest-ink pt-20 pb-24">
      <MetaTags
        title="Best Cancer Centers in the US by State | Top NCI Hospitals Directory"
        description="Explore top NCI-Designated Comprehensive Cancer Centers across all 50 US States. Find leading oncologists, cutting-edge clinical trials, second opinions, and navigation."
        path="/cancer-centers-by-state"
        jsonLd={jsonLd}
      />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-emerald-50/70 via-cream-paper to-cream-paper border-b border-border-mist py-16 px-6 sm:px-12 text-center">
        <div className="max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-100 text-emerald-900 text-xs sm:text-sm font-medium mb-5 border border-emerald-200/60 shadow-2xs">
            <ShieldCheck className="w-4 h-4 text-emerald-700" />
            <span>Official US National Oncology Directory</span>
          </div>

          <h1 className="text-4xl sm:text-6xl font-bold font-serif text-forest-ink tracking-tight mb-5 leading-tight">
            Best Cancer Centers in the US <br className="hidden sm:inline" />
            <span className="text-emerald-700">Across All 50 States</span>
          </h1>

          <p className="text-base sm:text-xl text-charcoal/85 max-w-2xl mx-auto mb-8 leading-relaxed font-sans">
            Navigate {TOTAL_US_NCI_CENTERS} NCI-Designated Comprehensive Cancer Centers, world-class academic hospital networks, and breakthrough clinical trials. Verified data for patients and families.
          </p>

          {/* Quick Search */}
          <div className="max-w-xl mx-auto relative mb-6">
            <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-charcoal/40" />
            <input
              type="text"
              placeholder="Search by state (e.g. New York, Texas, California, Ohio) or city..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-12 pr-4 py-4 rounded-xl bg-white text-forest-ink placeholder-charcoal/40 border border-border-mist shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-base"
            />
          </div>

          {/* Region Tabs */}
          <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
            {['All', 'Northeast', 'South', 'Midwest', 'West'].map((region) => (
              <button
                key={region}
                onClick={() => setSelectedRegion(region)}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                  selectedRegion === region
                    ? 'bg-forest-ink text-white shadow-sm'
                    : 'bg-white hover:bg-emerald-50 text-forest-ink border border-border-mist'
                }`}
              >
                {region === 'All' ? 'All 50 States & DC' : region}
              </button>
            ))}
          </div>

          {/* Quick Cornerstone SEO Links */}
          <div className="flex flex-wrap items-center justify-center gap-2.5 mt-8 pt-6 border-t border-border-mist text-xs">
            <span className="text-charcoal/60 uppercase tracking-wider font-semibold">Specialized National Hubs:</span>
            <Link
              to="/best-cancer-navigation-platform"
              className="px-3 py-1.5 rounded-full bg-white hover:bg-emerald-50 text-forest-ink border border-border-mist font-medium shadow-2xs transition-colors"
            >
              #1 Cancer Navigation Platform in US
            </Link>
            <Link
              to="/best-cancer-app"
              className="px-3 py-1.5 rounded-full bg-white hover:bg-emerald-50 text-forest-ink border border-border-mist font-medium shadow-2xs transition-colors"
            >
              #1 Cancer App & AI Agent
            </Link>
            <Link
              to="/best-medical-tourism-oncology"
              className="px-3 py-1.5 rounded-full bg-white hover:bg-emerald-50 text-forest-ink border border-border-mist font-medium shadow-2xs transition-colors"
            >
              Leading Medical Tourism Platform
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Ribbon */}
      <section className="bg-white border-y border-forest-ink/10 py-6 px-6 sm:px-12">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          <div>
            <div className="text-3xl font-bold font-serif text-forest-ink">50 + DC</div>
            <div className="text-xs uppercase tracking-wider text-forest-ink/60 font-semibold mt-1">States Covered</div>
          </div>
          <div>
            <div className="text-3xl font-bold font-serif text-emerald-600">68</div>
            <div className="text-xs uppercase tracking-wider text-forest-ink/60 font-semibold mt-1">NCI-Designated Centers</div>
          </div>
          <div>
            <div className="text-3xl font-bold font-serif text-forest-ink">10,000+</div>
            <div className="text-xs uppercase tracking-wider text-forest-ink/60 font-semibold mt-1">Open Clinical Trials</div>
          </div>
          <div>
            <div className="text-3xl font-bold font-serif text-emerald-600">24 / 7</div>
            <div className="text-xs uppercase tracking-wider text-forest-ink/60 font-semibold mt-1">AI Care Companion</div>
          </div>
        </div>
      </section>

      {/* Directory Grid */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 py-16">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold font-serif text-forest-ink">
              Select Your State
            </h2>
            <p className="text-forest-ink/70 text-sm mt-1">
              Showing {filteredStates.length} states with accredited cancer centers & hospital networks
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredStates.map((state) => (
            <motion.div
              key={state.slug}
              whileHover={{ y: -4 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-2xl p-6 border border-forest-ink/10 hover:border-emerald-500/40 hover:shadow-lg transition-all flex flex-col justify-between"
            >
              <div>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-xl font-bold text-forest-ink hover:text-emerald-700 transition-colors">
                      <Link to={`/cancer-centers/${state.slug}`} className="hover:underline flex items-center gap-2">
                        {state.name}
                        <span className="text-xs font-mono font-semibold px-2 py-0.5 rounded bg-forest-ink/5 text-forest-ink/70">
                          {state.abbr}
                        </span>
                      </Link>
                    </h3>
                    <p className="text-xs text-forest-ink/50 mt-0.5 flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5" />
                      {state.region} Region · Capital: {state.capital}
                    </p>
                  </div>
                  {state.nciCount > 0 ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">
                      <Award className="w-3 h-3" />
                      {state.nciCount} NCI {state.nciCount === 1 ? 'Center' : 'Centers'}
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                      Top Regional Networks
                    </span>
                  )}
                </div>

                <p className="text-sm text-forest-ink/70 line-clamp-2 mb-4 leading-relaxed">
                  {state.highlights}
                </p>

                {state.nciCenters.length > 0 && (
                  <div className="mb-4">
                    <div className="text-xs font-semibold text-forest-ink/80 mb-1.5 uppercase tracking-wide">
                      Premier Center:
                    </div>
                    <div className="text-xs bg-forest-ink/5 rounded-lg p-2 text-forest-ink/90 font-medium">
                      {state.nciCenters[0].name} ({state.nciCenters[0].city})
                    </div>
                  </div>
                )}
              </div>

              <div className="pt-4 border-t border-forest-ink/5 flex items-center justify-between">
                <span className="text-xs text-forest-ink/60">
                  {state.majorCities.slice(0, 3).join(', ')}
                </span>
                <Link
                  to={`/cancer-centers/${state.slug}`}
                  className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-700 hover:text-emerald-800"
                >
                  View Guide <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Care Matching Banner */}
      <section className="max-w-6xl mx-auto px-6 sm:px-12 my-12">
        <div className="bg-gradient-to-r from-forest-ink to-emerald-950 text-white rounded-3xl p-8 sm:p-12 text-center relative overflow-hidden shadow-xl">
          <div className="max-w-2xl mx-auto relative z-10">
            <h2 className="text-3xl sm:text-4xl font-bold font-serif text-white mb-4">
              Need Personalized Matching to the Best US Oncologist?
            </h2>
            <p className="text-emerald-100/90 text-base mb-8 leading-relaxed font-sans">
              Our clinical algorithms match your exact cancer diagnosis, stage, and genomic biomarkers to verified NCI specialists and clinical trials across all 50 states.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/get-matched"
                className="w-full sm:w-auto px-8 py-3.5 rounded-xl bg-emerald-400 hover:bg-emerald-300 text-forest-ink font-bold text-base transition-colors shadow-lg"
              >
                Get Matched With a Center
              </Link>
              <Link
                to="/talk"
                className="w-full sm:w-auto px-8 py-3.5 rounded-xl bg-white/15 hover:bg-white/25 text-white border border-white/20 font-semibold text-base transition-colors"
              >
                Ask ByOnco AI Companion
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
